import { createContext, useContext, useState, useEffect } from 'react'
import { v4 as uuidv4 } from 'uuid'

// Contexts
import { useAuthInfo } from '@propelauth/react'

const DocumentTypeContext = createContext([])

const createDocumentPathFromName = (name, orgId) => {
    return `${orgId}/templates/${name}`
}

const exampleProtocolDocumentExamples = [
    'C4671005 Protocol Amendment 2.docx',
    'C3601009 Protocol Amendment 2 Clean 23MAY2022.docx',
    'C3601002 Protocol Amendment 2 Clean 18May22.docx',
    'B9991003 Protocol Amendment 7_(clean) Final 05 September 2018.docx',
]

const exampleSAPDocumentExamples = [
    'C4671005 Statistical Analysis Plan Amendment v1.1 12 October 2021.docx',
    'C3601009 Final Statistical Analysis Plan 21 April 2020.docx',
    'C3601002 Final Statistical Analysis Plan v2 25JUN2020.docx',
    'B9991003 Statistical Analysis Plan V5_16Jul2018.docx',
]

const exampleTablesFiguresListsDocumentExamples = [
    'ADAE_S020.html',
    'ADDD_s001.html',
    'ADEG_NS501.pdf',
    'ADTTEH_S001_SUB_SER.html',
    'ADCM_S003.html',
    'adtteh_forest1_mitt1_n.pdf',
    'adtteh_f001_mitt.pdf',
    'adtteh_f001_mitt1.pdf',
    'adtteh_f001_mitt2.pdf',
    'adtteh_forest3_mitt2.pdf',
]

const exampleCSRDocumentExamples = [
    'C4671005 Study Report Body.docx',
    'C3601009-Clinical Study Report Body.pdf',
    'C3601002-Clinical Study Report Body.pdf',
    'B9991003 Study Report Body.docx',
]

const exampleTOLDocumentExamples = [
    'C4671005 LOT - CLEAN.xlsx',
    'C3601009 LOT - Clean.xlsx',
    'C3601002 LOT - Clean (1).xlsx',
    'B9991003 LOT - clean.xlsx',
]

const exampleProtocolSections = [
    '1. Title Page',
    '2. Table of Contents',
    '3. List of Abbreviations and Definitions',
    '4. Synopsis',
    '5. Background and Rationale',
    '6. Study Objectives and Purpose',
    '7. Study Design',
    '8. Study Population',
    '9. Study Treatments',
    '10. Study Procedures and Assessments',
    '11. Adverse Events and Safety Reporting',
    '12. Statistical Considerations',
    '13. Data Management',
    '14. Quality Assurance and Monitoring',
    '15. Ethics and Regulatory Considerations',
    '16. Investigator Responsibilities',
    '17. Sponsor Responsibilities',
    '18. Insurance and Compensation',
    '19. Publication Policy',
    '20. References',
    '21. Appendices',
]

const exampleSAPSections = [
    '1. Title Page',
    '2. Table of Contents',
    '3. List of Abbreviations and Definitions',
    '4. Introduction',
    '5. Study Objectives and Endpoints',
    '6. Study Design',
    '7. Sample Size Determination',
    '8. Randomization and Blinding',
    '9. Analysis Sets',
    '10. Statistical Methods',
    '11. Efficacy Analyses',
    '12. Safety Analyses',
    '13. Other Analyses',
    '14. Data Handling and Derivations',
    '15. Statistical Software',
    '16. Changes from Protocol-Planned Analysis',
    '17. References',
    '18. Appendices',
]

const exampleTFLSections = [
    '1. Title Page',
    '2. Table of Contents',
    '3. List of Abbreviations and Definitions',
    '4. Summary Tables',
    '5. Demographic and Baseline Characteristics Tables',
    '6. Disposition and Protocol Deviations Tables',
    '7. Efficacy Tables',
    '8. Safety Tables',
    '9. Pharmacokinetic and/or Pharmacodynamic Tables (if applicable)',
    '10. Figures (e.g., Kaplan-Meier curves, forest plots)',
    '11. Patient Listings',
    '12. Appendices (if applicable, e.g., mock shells or footnotes explanations)',
]

const exampleCSRSections = [
    '1. Title Page',
    '2. Synopsis',
    '3. Table of Contents',
    '4. List of Abbreviations and Definitions',
    '5. Ethics',
    '6. Study Administrative Structure',
    '7. Introduction',
    '8. Study Objectives',
    '9. Investigational Plan',
    '10. Study Patients',
    '11. Efficacy Evaluation',
    '12. Safety Evaluation',
    '13. Discussion and Overall Conclusions',
    '14. Tables, Figures, and Graphs Referenced in the Text',
    '15. Reference List',
    '16. Appendices',
]

const exampleTOLSections = [
    '1. Title Page',
    '2. Table of Contents',
    '3. List of Abbreviations and Definitions',
    '4. Subject Disposition Listings',
    '5. Protocol Deviations Listings',
    '6. Demographic and Baseline Characteristics Listings',
    '7. Medical History and Medication Listings',
    '8. Efficacy Data Listings',
    '9. Pharmacokinetic/Pharmacodynamic Listings',
    '10. Individual Subject Data Listings',
    '11. Appendices',
]

const exampleTemplateDocumentExamples = [
    'Template_Text.docx',
    'CSR_Template.pdf',
    'SAP_Template.pdf',
    'ArtosGo_Template.pdf',
]

const exampleTemplateSections = [
    '1. Title Page',
    '2. Synopsis',
    '3. Table of Contents',
    '4. List of Abbreviations and Definitions',
    '5. Ethics',
    '6. Introduction',
    '7. Study Objectives',
    '8. Study Design',
    '9. Study Population',
    '10. Efficacy Evaluation',
    '11. Safety Evaluation',
    '12. Discussion and Overall Conclusions',
    '13. Tables, Figures, and Graphs',
    '14. References',
    '15. Appendices',
]

const exampleDSURSections = [
    '1. Title Page',
    '2. Table of Contents',
    '3. List of Abbreviations and Definitions',
    '4. Executive Summary',
    '5. Introduction',
    '6. Study Objectives',
    '7. Study Design',
    '8. Study Population',
    '9. Efficacy Evaluation',
    '10. Safety Evaluation',        
    '11. Pharmacokinetic/Pharmacodynamic Evaluation',
    '12. Discussion and Overall Conclusions',
    '13. References',
    '14. Appendices',
]

function generateDocumentTypeExamples(orgId, examples) {
    return examples.map((example) => {
        return {
            name: createDocumentPathFromName(example, orgId),
            url: '',
        }
    })
}

function generateSectionExamples(examples) {
    return examples.map((example) => {
        return {
            id: uuidv4(),
            name: example,
        }
    })
}

const generateDefaultDocumentTypes = (orgId) => {
    return [
        {
            id: uuidv4(),
            document_type: 'Protocol',
            sections: generateSectionExamples(
                exampleProtocolSections
            ),
            document_type_examples: generateDocumentTypeExamples(
                orgId,
                exampleProtocolDocumentExamples
            ),
        },
        {
            id: uuidv4(),
            document_type: 'Statistical Analysis Plan',
            sections: generateSectionExamples(
                exampleSAPSections
            ),
            document_type_examples: generateDocumentTypeExamples(
                orgId,
                exampleSAPDocumentExamples
            ),
        },
        {
            id: uuidv4(),
            document_type: 'Clinical Study Report',
            sections: generateSectionExamples(
                exampleCSRSections
            ),
            document_type_examples: generateDocumentTypeExamples(
                orgId,
                exampleCSRDocumentExamples
            ),
        },
        {
            id: uuidv4(),
            document_type: 'Table of Lists',
            sections: generateSectionExamples(
                exampleTOLSections
            ),
            document_type_examples: generateDocumentTypeExamples(
                orgId,
                exampleTOLDocumentExamples
            ),
        },
        {
            id: uuidv4(),
            document_type: 'Tables, Figures, and Listings',
            sections: generateSectionExamples(
                exampleTFLSections
            ),
            document_type_examples: generateDocumentTypeExamples(
                orgId,
                exampleTablesFiguresListsDocumentExamples
            ),
        },
        {
            id: uuidv4(),
            document_type: 'Template',
            sections: generateSectionExamples(
                exampleTemplateSections
            ),
            document_type_examples: generateDocumentTypeExamples(
                orgId,
                exampleTemplateDocumentExamples
            ),
        },
        {
            id: uuidv4(),
            document_type: 'Exposure Database',
            sections: generateSectionExamples(
                exampleDSURSections
            ),
            document_type_examples: generateDocumentTypeExamples(
                orgId,
                exampleTemplateDocumentExamples
            ),
        },
        {
            id: uuidv4(),
            document_type: 'Safety Database',
            sections: generateSectionExamples(
                exampleDSURSections
            ),
            document_type_examples: generateDocumentTypeExamples(
                orgId,
                exampleTemplateDocumentExamples
            ),
        },
    ]
}

export function DocumentTypeProvider({ children }) {
    const [documentTypes, setDocumentTypes] = useState([])
    const authInfo = useAuthInfo()
    const orgId = authInfo.orgId

    useEffect(() => {
        setDocumentTypes(generateDefaultDocumentTypes(orgId))
    }, [])

    return (
        <DocumentTypeContext.Provider value={{ documentTypes, setDocumentTypes }}>
            {children}
        </DocumentTypeContext.Provider>
    )
}

export function useDocumentType() {
    return useContext(DocumentTypeContext)
}

